import type { PaymentSubState } from '@diamond/shared/types';
import type { UseFeatureFlagReturn } from '@diamond/shared/utils';

import {
  type PaymentDestination,
  type PaymentDestinationKind,
  buildOrderDetailPaymentProofRoute,
  buildPaymentInstructionRoute,
  resolveCheckoutPaymentDestination,
} from './payment-destination';

export const CHECKOUT_FF_WAIT_TIMEOUT_MS = 5000;
export const CHECKOUT_FF_WAIT_POLL_MS = 50;

export type PaymentInstructionFlagWaitResult =
  | { status: 'ready'; value: boolean }
  | { status: 'timed_out' }
  | { status: 'error'; message: string };

export type CheckoutDetailOutcome =
  | { status: 'ok'; paymentSubState: PaymentSubState }
  | { status: 'failed' };

export interface CheckoutSuccessNavigation {
  href: string;
  kind: PaymentDestinationKind;
  method: 'replace';
  paymentSubState: PaymentSubState;
  shouldTrackWfpResolved: boolean;
  reportReason?:
    | 'checkout-ff-timeout'
    | 'checkout-ff-error'
    | 'checkout-detail-resolution-failed';
}

export interface DecideCheckoutSuccessNavigationArgs {
  paymentMethod: string;
  flagWait: PaymentInstructionFlagWaitResult;
  orderMetaId: string;
  warehouse: string;
  detailOutcome: CheckoutDetailOutcome;
}

const legacyOrderDetailProof = (
  orderMetaId: string,
  warehouse: string
): CheckoutSuccessNavigation => ({
  href: buildOrderDetailPaymentProofRoute({ orderMetaId, warehouse }),
  kind: 'order_detail_proof',
  method: 'replace',
  paymentSubState: null,
  shouldTrackWfpResolved: false,
});

const fromDestination = (
  destination: PaymentDestination,
  paymentSubState: PaymentSubState,
  reportReason?: CheckoutSuccessNavigation['reportReason']
): CheckoutSuccessNavigation => ({
  href: destination.href,
  kind: destination.kind,
  method: 'replace',
  paymentSubState,
  shouldTrackWfpResolved:
    destination.kind === 'payment_instruction' ||
    destination.kind === 'order_detail_proof' ||
    destination.kind === 'order_detail',
  reportReason,
});

/**
 * Waits until payment-instruction FF leaves loading, or times out / errors.
 * Caller owns the flag snapshot getter (typically a React ref read).
 */
export const waitForPaymentInstructionFlag = async (
  getFlag: () => Pick<UseFeatureFlagReturn, 'loading' | 'value' | 'error'>,
  options?: { timeoutMs?: number; pollMs?: number }
): Promise<PaymentInstructionFlagWaitResult> => {
  const timeoutMs = options?.timeoutMs ?? CHECKOUT_FF_WAIT_TIMEOUT_MS;
  const pollMs = options?.pollMs ?? CHECKOUT_FF_WAIT_POLL_MS;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const flag = getFlag();
    if (flag.error) {
      return { status: 'error', message: flag.error };
    }
    if (!flag.loading) {
      return { status: 'ready', value: flag.value };
    }
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }

  const finalFlag = getFlag();
  if (finalFlag.error) {
    return { status: 'error', message: finalFlag.error };
  }
  if (!finalFlag.loading) {
    return { status: 'ready', value: finalFlag.value };
  }
  return { status: 'timed_out' };
};

/**
 * Pure post-checkout navigation decision for buyer checkout success.
 * Effects (router.replace, Sentry, tracking) stay in the caller.
 */
export const decideCheckoutSuccessNavigation = ({
  paymentMethod,
  flagWait,
  orderMetaId,
  warehouse,
  detailOutcome,
}: DecideCheckoutSuccessNavigationArgs): CheckoutSuccessNavigation => {
  if (paymentMethod !== 'bank_transfer') {
    return legacyOrderDetailProof(orderMetaId, warehouse);
  }

  let isPaymentInstructionEnabled = false;
  let reportReason: CheckoutSuccessNavigation['reportReason'];

  switch (flagWait.status) {
    case 'ready':
      isPaymentInstructionEnabled = flagWait.value;
      break;
    case 'timed_out':
      // Checkout-entry only: do not treat default-false loading as FF off.
      isPaymentInstructionEnabled = true;
      reportReason = 'checkout-ff-timeout';
      break;
    case 'error':
      isPaymentInstructionEnabled = true;
      reportReason = 'checkout-ff-error';
      break;
    default: {
      const _exhaustive: never = flagWait;
      return _exhaustive;
    }
  }

  if (!isPaymentInstructionEnabled) {
    return legacyOrderDetailProof(orderMetaId, warehouse);
  }

  if (detailOutcome.status === 'failed') {
    return {
      href: buildPaymentInstructionRoute({ orderMetaId, warehouse }),
      kind: 'payment_instruction',
      method: 'replace',
      paymentSubState: null,
      shouldTrackWfpResolved: false,
      reportReason: reportReason ?? 'checkout-detail-resolution-failed',
    };
  }

  const destination = resolveCheckoutPaymentDestination({
    isPaymentInstructionEnabled: true,
    orderMetaId,
    paymentSubState: detailOutcome.paymentSubState,
    warehouse,
  });

  return fromDestination(
    destination,
    detailOutcome.paymentSubState,
    reportReason
  );
};
